import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { saveAs } from "file-saver";
import { Spinner } from "react-bootstrap";
import NEW_API from "../../../Api/NewApi";
import CalendarInput from "../../Common/FormElements/CalendarInput/CalendarInput";
import Button from "../../Common/FormElements/Button/Button";
import DonutChart from "../../Dashboard/SoldProducts/Chart";
import StatCards from "./StatCards";
import { CATEGORY_META, formatEuro } from "./statisticsData";

/**
 * Page 1 — Stock & Logistique: Valeur Stock Comptable as of one date, per
 * category + % split, with PDF export.
 */
const StockStats = () => {
  const { t } = useTranslation("common");
  // Null by default — the backend then computes "as of today".
  const [date, setDate] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const abortRef = useRef(null);
  useEffect(() => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    (async () => {
      setLoading(true);
      try {
        const { data } = await NEW_API.get("api/statistics/stock", {
          params: {
            date: date ? moment(date).format("YYYY-MM-DD") : undefined,
          },
          signal: abortRef.current.signal,
        });
        setStats(data?.data);
      } catch (error) {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
          return;
        console.log(error);
      } finally {
        setLoading(false);
      }
    })();
    return () => abortRef.current?.abort();
  }, [date]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const response = await NEW_API.post(
        "api/statistics/stock-pdf",
        {
          data: {
            date: date ? moment(date).format("YYYY-MM-DD") : undefined,
          },
        },
        { responseType: "blob" }
      );
      saveAs(
        new Blob([response.data], { type: "application/pdf" }),
        `stock_${moment(date ?? undefined).format("YYYY-MM-DD")}.pdf`
      );
    } catch (error) {
      console.log(error);
    } finally {
      setExporting(false);
    }
  };

  const cards = [
    {
      label: "Accounting stock value",
      value: formatEuro(stats?.total),
      bgColor: "#345d68",
    },
    ...CATEGORY_META.map(({ key, label, color }) => ({
      label,
      value: formatEuro(stats?.byCategory?.[key]),
      suffix: ` (${stats?.percentages?.[key] ?? 0}%)`,
      bgColor: color,
    })),
  ];

  return (
    <div className="position-relative">
      {loading && (
        <div className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-50 z-9999 d-flex align-items-center justify-content-center">
          <Spinner />
        </div>
      )}
      <div className="d-flex align-items-end flex-wrap gap-3 mb-2">
        <h3 className="text-blue bold f18 quickSand mb-0 me-auto">
          {t("Stock & Logistics")}
        </h3>
        <div className="max-w-200px w-100">
          <CalendarInput
            name="date"
            value={date}
            withMinDate={false}
            minDate={false}
            callback={(e) => e.target.value && setDate(e.target.value)}
          />
        </div>
        <Button
          variant="outline"
          loading={exporting}
          Icon={() => <i className="bi bi-download fa-lg" />}
          onClick={exportPdf}
        >
          {t("Export PDF")}
        </Button>
      </div>
      <StatCards featuredFirst cards={cards} />
      <div className="max-w-500px mx-auto">
        <DonutChart
          data={CATEGORY_META.map(({ key }) => stats?.byCategory?.[key] ?? 0)}
          labels={CATEGORY_META.map(({ label }) => t(`${label}`))}
        />
      </div>
    </div>
  );
};

export default StockStats;
